import { Field, FieldError, FieldLabel, Textarea } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import type {
  DiagnosticReport,
  Patient as PatientResource,
} from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import i18next from "i18next";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useFinalizeReview } from "../../network/useFinalizeReview";
import { ResultSummary } from "./ResultSummary";

const DEFAULT_PATIENT_MESSAGE = "Hi {{name}}, your lab results are ready.";

const finalizeReviewFormSchema = z.object({
  messageForPatient: z
    .string()
    .min(1, { message: "Message for patient is required" }),
});

/**
 * Lab results review form for async-labs Universal Clinic patients. These
 * patients have no triage level — the review is finalized through a dedicated
 * endpoint and is only available when the patient is reachable for outreach.
 */
export const UniversalClinicResultsReviewForm = ({
  patientId,
  serviceRequestId,
  diagnosticReport,
  patient,
}: {
  patientId: string;
  serviceRequestId: string;
  diagnosticReport?: DiagnosticReport;
  patient?: PatientResource;
}) => {
  const { mutate: finalizeReview, isPending: isSubmitting } = useFinalizeReview(
    {
      patientId,
      serviceRequestId,
    },
  );

  const form = useForm({
    resolver: zodResolver(finalizeReviewFormSchema),
    defaultValues: {
      messageForPatient: "",
    },
  });

  useEffect(() => {
    if (patient?.firstName && !form.getValues("messageForPatient")) {
      form.setValue(
        "messageForPatient",
        i18next.t(DEFAULT_PATIENT_MESSAGE, { name: patient.firstName }),
      );
    }
  }, [patient?.firstName, form]);

  const handleSubmit = (values: z.infer<typeof finalizeReviewFormSchema>) => {
    finalizeReview({ messageForPatient: values.messageForPatient });
  };

  return (
    <form className="grid gap-2" onSubmit={form.handleSubmit(handleSubmit)}>
      <Controller
        control={form.control}
        name="messageForPatient"
        render={({ field, fieldState }) => (
          <Field>
            <FieldLabel htmlFor="messageForPatient">
              Message for Patient
            </FieldLabel>
            <Textarea id="messageForPatient" {...field} />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      <ResultSummary
        diagnosticReport={diagnosticReport}
        patientId={patientId}
      />

      <Button
        size="xs"
        type="submit"
        className="w-fit justify-self-end"
        isLoading={isSubmitting}
      >
        Submit Review
      </Button>
    </form>
  );
};
